import * as React from "react";
import formatDate from "dateformat";
import { ResponsiveContainer, LineChart, Line } from "recharts";
import LoadingState from "../../../../ui-library/loading-state/loading-state";
import { Colors, RainBow } from "../../../../design-tokens/colors";
import ContentCard from "../../../../ui-library/content-card/content-card";
import CardContent from "../../../../ui-library/content-card/card-content";
import CardHeading from "../../../../ui-library/content-card/card-heading";
import CardSubHeading from "../../../../ui-library/content-card/card-sub-heading";
import ItemPicker from "../../../../ui-library/item-picker/item-picker";
import { EventCategory } from "../../../../../utils/analytics";
import SourceLink from "../../../../ui-library/source-link/source-link";
import { GetResponseTimelineByCountry_responsesChangeTimelineByCountry_data } from "../../../../../apollo-types/GetResponseTimelineByCountry";
import TextAnswerTip from "../../../../ui-library/answers/text-answer-tip";
import { GetResponseTimeSeriesByCountry_responsesTimeSeriesByCountry_data } from "../../../../../apollo-types/GetResponseTimeSeriesByCountry";
import { COVID_SOURCES } from "../../../../../constants/sources";
import { POLICY_VALUE_DETAIL } from "./policy-value-explaination";
import ResponseTableBody from "./response-table-body";
import styles from "./government-response-vs-cases.styles";
import {
  processTimelineData,
  getDatePoints,
  ProcessedTimePoint,
} from "./process-timeline-data";
import ResponseSeriesTableBody from "./response-series-table-body";
import ResponseSeriesLegends from "./response-series-legend";

const colorMapping: { [key: string]: string } = {
  C: Colors.Red,
  E: RainBow.SkyBlue,
  H: RainBow.Teal,
};

interface GovernmentResponseVsCasesProps {
  countryName: string;
  responseTimeline?:
    | GetResponseTimelineByCountry_responsesChangeTimelineByCountry_data[]
    | null;
  responseTimeSeries?:
    | GetResponseTimeSeriesByCountry_responsesTimeSeriesByCountry_data[]
    | null;
  loading?: boolean;
  data?:
    | {
        date: string;
        averageNewCases: number;
        totalCases?: number | null;
      }[]
    | null;
}

const tabs = [
  {
    value: "C",
    label: "Containment & closure",
  },
  {
    value: "E",
    label: "Economic",
  },
  {
    value: "H",
    label: "Health system",
  },
  {
    value: "Response index",
    label: "Response index",
  },
];

const GovernmentResponseVsCases: React.FC<GovernmentResponseVsCasesProps> = ({
  responseTimeline,
  responseTimeSeries,
  loading,
  countryName,
  data,
}) => {
  const [activeTab, setActiveTab] = React.useState<string>(tabs[0].value);
  const [activePolicy, setActivePolicy] = React.useState<
    ProcessedTimePoint | undefined
  >();

  const startIndex = data?.findIndex((d) => d.totalCases !== null) || 0;
  const dataToPlot = data?.slice(startIndex, data.length);
  const startDate = dataToPlot ? dataToPlot[0].date : "01/01/2020";
  const currentDate = dataToPlot
    ? dataToPlot[dataToPlot.length - 1].date
    : "01/01/2020";

  const processedTimeline =
    responseTimeline &&
    processTimelineData({
      startDate,
      currentDate,
      responseTimeline,
    });

  const headerDates = getDatePoints({ startDate, currentDate });
  const headingDetail = `Last updated ${formatDate(
    currentDate,
    "dd mmm yyyy"
  )}`;

  const activePolicyValues =
    activePolicy &&
    POLICY_VALUE_DETAIL[activePolicy.policyType.substring(0, 2)];

  if (!loading && (!responseTimeline || !responseTimeSeries)) {
    return null;
  }

  return (
    <ContentCard>
      <CardContent>
        <CardHeading headingDetail={headingDetail}>
          Government responses versus COVID-19 cases
        </CardHeading>
        <CardSubHeading>
          Find out what policies {countryName} has implemented to combat the
          pandemic.
        </CardSubHeading>

        {loading && <LoadingState />}

        {data && processedTimeline && responseTimeSeries && (
          <>
            <ItemPicker
              itemPickerName="resVsCaseItemPicker"
              isScrollable={true}
              isUniWidth={true}
              items={tabs}
              activeItemValue={activeTab}
              onItemClick={(value) => {
                setActiveTab(value);
                setActivePolicy(undefined);
              }}
              eventCategory={EventCategory.CountryGovResponseVsCases}
            />
            <div id="govResTable" css={styles.tableOutMostContainer}>
              <div css={styles.tableOuterContainer}>
                <div css={styles.tableContainer}>
                  <div css={styles.header}>
                    {headerDates.map((d) => (
                      <div key={d}>{d}</div>
                    ))}
                  </div>
                  <div css={styles.tableBody}>
                    <div css={styles.chartBackground}>
                      <ResponsiveContainer
                        key={activeTab}
                        width="100%"
                        height="100%"
                      >
                        <LineChart
                          data={dataToPlot}
                          margin={{ top: 20, bottom: 20, left: 0, right: 0 }}
                        >
                          <Line
                            type="basis"
                            dataKey="averageNewCases"
                            name={name}
                            stroke={Colors.Pink}
                            strokeWidth={3}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {activeTab !== "Response index" && (
                      <ResponseTableBody
                        countryName={countryName}
                        policyTimeline={processedTimeline[activeTab]}
                        policyType={activeTab}
                        color={colorMapping[activeTab]}
                        activePolicy={activePolicy}
                        onClickPolicy={setActivePolicy}
                      />
                    )}

                    {activeTab === "Response index" && (
                      <ResponseSeriesTableBody
                        startDate={startDate}
                        responseTimeSeries={responseTimeSeries}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            {activePolicy && activePolicyValues && (
              <>
                <div
                  css={[
                    styles.timeLabel,
                    {
                      backgroundColor: colorMapping[activeTab],
                    },
                  ]}
                >
                  {formatDate(activePolicy.date, "mmm dS yyyy")}
                </div>
                <div css={styles.policyHeading}>{activePolicyValues.title}</div>
                {activePolicyValues.measurement === "ordinal" && (
                  <div css={styles.policyDetail}>
                    Level {activePolicy.policyValue} of{" "}
                    {activePolicyValues.coding.length - 1} -{" "}
                    {activePolicyValues.coding[activePolicy.policyValue]}
                  </div>
                )}
                {activePolicyValues.measurement === "usd" && (
                  <div css={styles.policyDetail}>
                    {activePolicy.policyValue} USD
                  </div>
                )}
                <hr />
                <div
                  css={styles.eventDescription}
                  className="content"
                  dangerouslySetInnerHTML={{ __html: activePolicy.notes }}
                />
              </>
            )}

            {activeTab === "Response index" && (
              <>
                <ResponseSeriesLegends />
                <div css={styles.resIndexDetail}>
                  Response index value scale from 0 to 100 (100 = strictest
                  response). See{" "}
                  <a
                    href="https://github.com/OxCGRT/covid-policy-tracker/blob/master/documentation/index_methodology.md"
                    target="_blank"
                    rel="noreferrer"
                  >
                    how the values are calculated
                  </a>
                  .
                </div>
              </>
            )}
            <TextAnswerTip
              title="About government response data"
              description="Data is collected from public sources by a team of over one hundred Oxford University students and staff from every part of the world. Please note: not all countries are equally up to date; for certain countries, some indicators will be missing for some days."
            />
            <SourceLink
              sources={[COVID_SOURCES.Oxford]}
              eventCategory={EventCategory.CountryGovResponseVsCases}
            />
          </>
        )}
      </CardContent>
    </ContentCard>
  );
};

export default GovernmentResponseVsCases;
